import { AdCreativeType, FBCallToAction, isHttpUrl } from '@marketing-milk/interfaces'
import { LocalAdPreviewCreative } from '../../../../../components/ad-preview/local'
import { RowMediaPreview, RowUIState } from '../state'
import { DEFAULT_DYNAMIC_SELECTION, DynamicSelection, clampSelection } from './dynamicSelection'

/** Hostname of a valid http(s) URL, for the preview's display-URL line; else undefined. */
function displayURL(destinationURL: string): string | undefined {
  const trimmed = destinationURL.trim()
  if (!trimmed || !isHttpUrl(trimmed)) return undefined
  try {
    return new URL(trimmed).hostname
  } catch {
    return undefined
  }
}

/** The creative-level copy a one-asset ad card shows, resolved from wherever it came from. */
interface PreviewCopy {
  primaryText: string
  headline: string
  description: string
  callToAction: string
  destinationURL: string
}

/**
 * One asset plus its copy, as an ad card.
 *
 * Shared by the single-image/video branch and the dynamic branch: a dynamic ad
 * with one asset and one variant pinned from each pool is structurally the same
 * card, so the two must not drift.
 *
 * `fallbackFormat` is used only when there's no decoded preview to render — it
 * keeps the row's true format so `LocalAdPreview` shows its honest "unavailable"
 * fallback rather than an empty image.
 */
function singleAssetPreview(
  preview: RowMediaPreview | undefined,
  copy: PreviewCopy,
  fallbackFormat: AdCreativeType
): LocalAdPreviewCreative {
  const rendersAsVideo = preview?.kind === 'video'
  return {
    // The preview's kind decides the render, not the ad format. A .gif is a
    // single_video ad but must render as an image (a GIF blob won't play in a
    // `<video>`), and a video whose blob didn't decode has no preview at all.
    format: preview
      ? rendersAsVideo
        ? AdCreativeType.single_video
        : AdCreativeType.single_image
      : fallbackFormat,
    primaryText: copy.primaryText,
    imageURL: rendersAsVideo ? undefined : preview?.sourceUrl,
    singleVideo: rendersAsVideo
      ? { storedSource: preview.sourceUrl, thumbnail: preview.posterUrl }
      : undefined,
    carouselSlides: undefined,
    linkHeadline: copy.headline,
    linkDescription: copy.description,
    // The dropdown only offers SUPPORTED_CALL_TO_ACTIONS, so this string is a valid enum value.
    callToAction: copy.callToAction as FBCallToAction,
    linkDisplayURL: displayURL(copy.destinationURL),
  }
}

/**
 * Adapts a Review row's live edit state into the shape `LocalAdPreview` reads, so
 * the FB-feed mock updates as the AE types. The creative *name* is intentionally
 * omitted — it's an internal label and never appears in a Facebook ad.
 *
 * The media comes from the extracted previews rather than the format: a `.gif`
 * imports through Facebook's video pipeline but must render as an image locally
 * (a GIF blob in a `<video>` element won't play), which is exactly the split
 * `RowMediaPreview.kind` carries.
 */
export function toPreviewCreative(
  ui: RowUIState,
  format: AdCreativeType,
  previews: RowMediaPreview[] = [],
  selection: DynamicSelection = DEFAULT_DYNAMIC_SELECTION
): LocalAdPreviewCreative {
  if (format === AdCreativeType.carousel) {
    return {
      format,
      primaryText: ui.editedPrimaryText,
      // One card per slide, taking its media from the preview at the same
      // ordinal and its copy from the slide edit state — so a card can show its
      // media before its copy is written, and vice versa.
      carouselSlides: (ui.editedSlides ?? []).map((slide, ordinal) => {
        const preview = previews.find(item => item.ordinal === ordinal)
        return {
          linkHeadline: slide.headline,
          linkDescription: slide.description,
          linkWebsiteURL: slide.destinationUrl || ui.editedDestinationURL,
          ...(preview?.kind === 'video'
            ? { mediaType: 'video' as const, video: { thumbnail: preview.posterUrl } }
            : preview
            ? { mediaType: 'image' as const, image: { url: preview.sourceUrl } }
            : {}),
        }
      }),
      callToAction: ui.editedCallToAction as FBCallToAction,
      linkDisplayURL: displayURL(ui.editedDestinationURL),
    }
  }

  if (format === AdCreativeType.dynamic) {
    // A dynamic ad has no single rendering — Facebook mixes one variant from each
    // pool with one asset. `selection` pins one such combination so the card can
    // draw it; it's a view control and reaches nothing that gets imported.
    const pools = ui.editedCopyPools
    const pinned = clampSelection(selection, pools, previews.length)
    // Falling back to the row-level field when a pool is empty matches what the
    // editor promises there: "the ad's single X will be used".
    return singleAssetPreview(
      previews[pinned.asset],
      {
        primaryText: pools?.primaryTexts[pinned.primaryTexts] ?? ui.editedPrimaryText,
        headline: pools?.headlines[pinned.headlines] ?? ui.editedHeadline,
        description: pools?.descriptions[pinned.descriptions] ?? ui.editedDescription,
        callToAction: pools?.callToActions[pinned.callToActions] ?? ui.editedCallToAction,
        destinationURL: ui.editedDestinationURL,
      },
      format
    )
  }

  return singleAssetPreview(
    previews[0],
    {
      primaryText: ui.editedPrimaryText,
      headline: ui.editedHeadline,
      description: ui.editedDescription,
      callToAction: ui.editedCallToAction,
      destinationURL: ui.editedDestinationURL,
    },
    format
  )
}
